import {
    type InflowSegments,
    buildInflowSegments,
    computeInflowCdf,
} from "./ParticleInflow";
import {
    PARTICLE_COLORS,
    PARTICLE_HEIGHT,
    PARTICLE_START_COUNT,
    PARTICLE_STEPS_PER_FT,
    PARTICLE_WIDTH,
    type ParticleField,
    type ParticleGeometry,
    type ParticleRenderer,
    type ParticleSettings,
    particleCount,
    seededRandom,
    warmupWindowSteps,
} from "./ParticleTypes";

export class Particle2D implements ParticleRenderer {
    readonly kind = "2d" as const;
    private field!: ParticleField;
    private geometry!: ParticleGeometry;
    private settings!: ParticleSettings;
    private count = PARTICLE_START_COUNT;
    private renderedCount = PARTICLE_START_COUNT - 1;
    private particleN = 0;
    private x = new Float32Array();
    private y = new Float32Array();
    private age = new Float32Array();
    private windU = new Float32Array();
    private windV = new Float32Array();
    private readonly trail = document.createElement("canvas");
    private readonly colored = document.createElement("canvas");
    private readonly colorField = document.createElement("canvas");
    private trailContext!: CanvasRenderingContext2D;
    private coloredContext!: CanvasRenderingContext2D;
    private colorContext!: CanvasRenderingContext2D;
    private colorPixels!: ImageData;
    private inflow: InflowSegments | null = null;
    private inflowCdf = new Float32Array();
    private inflowRate = 0;
    private inflowFt = -1;

    constructor(private readonly presentation: HTMLCanvasElement) {}

    configure(
        field: ParticleField,
        geometry: ParticleGeometry,
        settings: ParticleSettings,
    ) {
        this.field = field;
        this.geometry = geometry;
        this.settings = settings;
        this.reset();
    }

    reset() {
        if (!this.field) {
            return;
        }
        this.count = PARTICLE_START_COUNT;
        this.renderedCount = PARTICLE_START_COUNT - 1;
        this.trail.width = this.geometry.backWidth;
        this.trail.height = this.geometry.backHeight;
        this.colored.width = this.geometry.backWidth;
        this.colored.height = this.geometry.backHeight;
        this.colorField.width = this.field.width;
        this.colorField.height = this.field.height;
        this.trailContext = this.trail.getContext("2d")!;
        this.coloredContext = this.colored.getContext("2d")!;
        this.colorContext = this.colorField.getContext("2d")!;
        this.colorPixels = this.colorContext.createImageData(
            this.field.width,
            this.field.height,
        );
        const n = this.field.width * this.field.height;
        this.windU = new Float32Array(n);
        this.windV = new Float32Array(n);
        this.inflow = buildInflowSegments(this.field, this.geometry);
        this.inflowCdf = new Float32Array(this.inflow.n + 1);
        this.inflowRate = 0;
        this.inflowFt = -1;
        this.particleN = particleCount(
            this.field,
            this.geometry,
            this.settings.density,
        );
        this.x = new Float32Array(this.particleN);
        this.y = new Float32Array(this.particleN);
        this.age = new Float32Array(this.particleN);
        const random = seededRandom();
        for (let index = 0; index < this.particleN; index++) {
            const point = this.randomPoint(random);
            this.x[index] = point.x;
            this.y[index] = point.y;
            this.age[index] = Math.floor(
                random() * this.settings.age * PARTICLE_STEPS_PER_FT,
            );
        }
        this.clearPresentation();
    }

    renderTo(ft: number) {
        const target = Math.max(
            0,
            Math.round((ft - this.field.startFt) * PARTICLE_STEPS_PER_FT),
        );
        if (target < this.renderedCount) {
            this.reset();
        }
        const window = warmupWindowSteps(this.settings);
        while (this.count <= target) {
            // ウォームアップ (残りが減衰窓より遠い) 中は描画を省き状態のみ進める
            this.step(target - this.count < window);
        }
        this.present();
    }

    step(drawTrail = true) {
        const relativeFt = Math.max(this.count / PARTICLE_STEPS_PER_FT, 0);
        this.updateWind(relativeFt, drawTrail);
        const ftA = Math.floor(relativeFt);
        if (this.inflow && ftA !== this.inflowFt) {
            this.inflowRate = computeInflowCdf(
                this.inflow,
                this.field,
                ftA,
                this.geometry,
                this.settings,
                this.inflowCdf,
            );
            this.inflowFt = ftA;
        }
        const { width } = this.field;
        const { gridDx, gridDy, scale, rx, ry } = this.geometry;
        const invX = 1 / (gridDx * scale);
        const invY = 1 / (gridDy * scale);
        const speedScale = (3600 / PARTICLE_STEPS_PER_FT) * scale;
        const random = seededRandom((this.count + 4096) * 2654435761);

        if (drawTrail) {
            this.trailContext.save();
            this.trailContext.globalCompositeOperation = "destination-in";
            this.trailContext.fillStyle = `rgba(0,0,0,${Math.pow(
                10,
                -1 / Math.pow(this.settings.length, 1.5),
            )})`;
            this.trailContext.fillRect(
                0,
                0,
                this.trail.width,
                this.trail.height,
            );
            this.trailContext.restore();
            this.trailContext.save();
            this.trailContext.globalCompositeOperation = "source-over";
            this.trailContext.strokeStyle = "#ffffff";
            this.trailContext.lineWidth = Math.max(
                this.settings.lineWidth,
                0.2,
            );
            this.trailContext.lineCap = "round";
            this.trailContext.beginPath();
        }

        for (let index = 0; index < this.particleN; index++) {
            let px = this.x[index];
            let py = this.y[index];
            let gx = (px - rx) * invX;
            let gy = (py - ry) * invY;
            let x0 = Math.floor(gx);
            let y0 = Math.floor(gy);
            if (
                this.age[index] > this.settings.age * PARTICLE_STEPS_PER_FT ||
                !this.validCell(x0, y0)
            ) {
                const point = this.respawnPoint(random);
                px = point.x;
                py = point.y;
                this.x[index] = px;
                this.y[index] = py;
                this.age[index] = 0;
                gx = (px - rx) * invX;
                gy = (py - ry) * invY;
                x0 = Math.floor(gx);
                y0 = Math.floor(gy);
            }
            const xr = gx - x0;
            const yr = gy - y0;
            const offset = y0 * width + x0;
            const r00 = (1 - yr) * (1 - xr);
            const r01 = (1 - yr) * xr;
            const r10 = yr * (1 - xr);
            const r11 = yr * xr;
            const u =
                this.windU[offset] * r00 +
                this.windU[offset + 1] * r01 +
                this.windU[offset + width] * r10 +
                this.windU[offset + width + 1] * r11;
            const v =
                this.windV[offset] * r00 +
                this.windV[offset + 1] * r01 +
                this.windV[offset + width] * r10 +
                this.windV[offset + width + 1] * r11;
            const nx = px + u * speedScale * this.settings.speed;
            const ny = py - v * speedScale * this.settings.speed;
            if (drawTrail) {
                this.trailContext.moveTo(px, py);
                this.trailContext.lineTo(nx, ny);
            }
            this.x[index] = nx;
            this.y[index] = ny;
            this.age[index]++;
        }
        if (drawTrail) {
            this.trailContext.stroke();
            this.trailContext.restore();
        }
        this.renderedCount = this.count;
        this.count++;
        return this.getCurrentFt();
    }

    present() {
        this.colorContext.putImageData(this.colorPixels, 0, 0);
        this.coloredContext.clearRect(
            0,
            0,
            this.colored.width,
            this.colored.height,
        );
        this.coloredContext.globalCompositeOperation = "source-over";
        this.coloredContext.drawImage(this.trail, 0, 0);
        this.coloredContext.globalCompositeOperation = "source-in";
        this.coloredContext.imageSmoothingEnabled = true;
        this.coloredContext.drawImage(
            this.colorField,
            this.geometry.rx,
            this.geometry.ry,
            this.geometry.rw,
            this.geometry.rh,
        );
        this.coloredContext.globalCompositeOperation = "source-over";

        const context = this.presentation.getContext("2d")!;
        context.clearRect(0, 0, PARTICLE_WIDTH, PARTICLE_HEIGHT);
        if (this.geometry.degree === 0) {
            context.drawImage(this.colored, 0, 0);
        } else {
            context.save();
            context.translate(PARTICLE_WIDTH / 2, PARTICLE_HEIGHT / 2);
            context.rotate((this.geometry.degree / 180) * Math.PI);
            context.translate(-PARTICLE_WIDTH / 2, -PARTICLE_HEIGHT / 2);
            context.drawImage(
                this.colored,
                this.geometry.rx0,
                this.geometry.ry0,
            );
            context.restore();
        }
    }

    getCurrentFt() {
        return (
            this.field.startFt +
            Math.max(this.renderedCount, 0) / PARTICLE_STEPS_PER_FT
        );
    }

    dispose() {
        this.clearPresentation();
    }

    private updateWind(relativeFt: number, buildColors = true) {
        const n = this.field.width * this.field.height;
        const a = Math.min(
            Math.max(Math.floor(relativeFt), 0),
            this.field.timeCount - 1,
        );
        const b = Math.min(a + 1, this.field.timeCount - 1);
        const fraction = Math.min(Math.max(relativeFt - a, 0), 1);
        const colors = PARTICLE_COLORS[this.settings.colorMode];
        const pixels = this.colorPixels.data;
        for (let index = 0; index < n; index++) {
            const ua = this.field.u[a * n + index];
            const va = this.field.v[a * n + index];
            const ub = this.field.u[b * n + index];
            const vb = this.field.v[b * n + index];
            const valid = ua !== 32767 && ub !== 32767;
            const u = valid
                ? (ua * (1 - fraction) + ub * fraction) / 100000
                : 0;
            const v = valid
                ? (va * (1 - fraction) + vb * fraction) / 100000
                : 0;
            this.windU[index] = u;
            this.windV[index] = v;
            if (!buildColors) {
                continue;
            }
            const projection = this.field.projection[index] + 640;
            const speed = Math.hypot(u * projection, v * projection);
            const level = Math.min(
                6,
                Math.floor(speed / this.settings.colorThreshold),
            );
            const color = colors[level];
            const pixel = index * 4;
            pixels[pixel] = color[0];
            pixels[pixel + 1] = color[1];
            pixels[pixel + 2] = color[2];
            pixels[pixel + 3] = valid ? 255 : 0;
        }
    }

    private validCell(x: number, y: number) {
        if (
            x < 0 ||
            y < 0 ||
            x >= this.field.width - 1 ||
            y >= this.field.height - 1
        ) {
            return false;
        }
        const offset = y * this.field.width + x;
        return (
            this.field.u[offset] !== 32767 &&
            this.field.u[offset + 1] !== 32767 &&
            this.field.u[offset + this.field.width] !== 32767 &&
            this.field.u[offset + this.field.width + 1] !== 32767
        );
    }

    // 境界流入つき再発生 (旧 2D 実装の ex_rate 混合)。乱数 < ex_rate なら
    // 流入 CDF の逆引きで外周線分上に、それ以外は一様に発生する。
    private respawnPoint(random: () => number) {
        const inflow = this.inflow;
        if (inflow && this.inflowRate > 0 && random() < this.inflowRate) {
            const r = random();
            const cdf = this.inflowCdf;
            let lo = 0;
            let hi = inflow.n - 1;
            while (lo < hi) {
                const mid = (lo + hi) >> 1;
                if (cdf[mid + 1] < r) {
                    lo = mid + 1;
                } else {
                    hi = mid;
                }
            }
            const k = lo * 4;
            const t = random();
            return {
                x: inflow.xy[k] + inflow.xy[k + 2] * t,
                y: inflow.xy[k + 1] + inflow.xy[k + 3] * t,
                valid: true,
            };
        }
        return this.randomPoint(random);
    }

    private randomPoint(random: () => number) {
        for (let attempt = 0; attempt < 100; attempt++) {
            const x = this.geometry.rx + random() * this.geometry.rw;
            const y = this.geometry.ry + random() * this.geometry.rh;
            const gx = Math.floor(
                (x - this.geometry.rx) /
                    (this.geometry.gridDx * this.geometry.scale),
            );
            const gy = Math.floor(
                (y - this.geometry.ry) /
                    (this.geometry.gridDy * this.geometry.scale),
            );
            if (this.validCell(gx, gy)) {
                return { x, y };
            }
        }
        return { x: this.geometry.rx, y: this.geometry.ry };
    }

    private clearPresentation() {
        this.presentation
            .getContext("2d")
            ?.clearRect(0, 0, PARTICLE_WIDTH, PARTICLE_HEIGHT);
    }
}
